import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Badge, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { formatDateTime } from '../utils/dateFormat';

const REASONS = { NOT_AS_DESCRIBED: 'Not as described', COUNTERFEIT: 'Fake or a copy', PROHIBITED: 'Should not be sold', WRONG_PRICE: 'Wrong or misleading price', OTHER: 'Something else' };
const OUTCOMES = { DISMISSED: 'Dismissed', PRODUCT_HIDDEN: 'Product taken off', STORE_HIDDEN: 'Store taken off' };
const messageOf = (e, fallback) => e.response?.data?.message || (typeof e.response?.data === 'string' ? e.response.data : null) || fallback;

/**
 * Tech Support > Shop Directory: the stores that show their products on ibigdo.com/shop, what buyers have reported, and taking a store or a product off it (or back on). Taking
 * something off changes only that page: the store, its address and its orders carry on.
 */
export default function ShopDirectoryAdmin() {
  const [data, setData] = useState(null);
  const [view, setView] = useState('OPEN');
  const [busy, setBusy] = useState(false);
  const [takeOff, setTakeOff] = useState(null); // the store being taken off, asking for a reason
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      setData((await axios.get('/api/system-admin/shop-directory')).data);
    } catch (e) {
      toast.error(messageOf(e, 'Could not load the shop directory.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const call = async (request, done) => {
    setBusy(true);
    try {
      setData((await request()).data);
      if (done) toast.success(done);
    } catch (e) {
      toast.error(messageOf(e, 'That did not work.'));
    } finally {
      setBusy(false);
    }
  };

  const putBack = (s) => call(() => axios.put(`/api/system-admin/shop-directory/stores/${s.organizationId}/hidden`, { hidden: false }), 'Put back on the shop page.');
  const confirmTakeOff = async (e) => {
    e.preventDefault();
    const s = takeOff;
    setTakeOff(null);
    await call(() => axios.put(`/api/system-admin/shop-directory/stores/${s.organizationId}/hidden`, { hidden: true, reason }), 'Taken off the shop page.');
    setReason('');
  };
  const resolve = (r, action, done) => call(() => axios.post(`/api/system-admin/shop-directory/reports/${r.id}/resolve`, { action }), done);

  if (!data) return <p className="text-muted mt-3">Loading...</p>;
  const reports = data.reports.filter((r) => (view === 'OPEN' ? r.status === 'NEW' : view === 'DONE' ? r.status !== 'NEW' : true));

  return (
    <div className="mt-3" data-testid="shop-directory-admin">
      <p className="text-muted">
        Stores that chose to show their products on ibigdo.com/shop, and what buyers have reported. A report takes nothing off by itself: look at the product, then dismiss it or take the product (or the store) off.
        The store, its address and its orders are not affected.
      </p>

      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Stores</h5></Card.Header>
        {data.stores.length === 0 ? <Card.Body className="text-muted">No store has chosen to be on the shop page yet.</Card.Body> : (
          <Table hover responsive className="mb-0 align-middle" data-testid="directory-stores">
            <thead><tr><th>Store</th><th>On the page</th><th>Products shown</th><th>Open reports</th><th /></tr></thead>
            <tbody>
              {data.stores.map((s) => (
                <tr key={s.organizationId}>
                  <td><a href={`/shop/${s.slug}`} target="_blank" rel="noopener noreferrer">{s.name}</a><div className="small text-muted">org {s.organizationId}</div></td>
                  <td>
                    {s.hidden ? <Badge bg="danger">Taken off</Badge> : s.listProducts ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}
                    {s.hidden && s.hiddenReason && <div className="small text-muted">{s.hiddenReason}</div>}
                  </td>
                  <td>{s.productsShown}</td>
                  <td>{s.openReports > 0 ? <Badge bg="warning" text="dark">{s.openReports}</Badge> : 0}</td>
                  <td className="text-end">
                    {s.hidden
                      ? <Button size="sm" variant="outline-success" disabled={busy} onClick={() => putBack(s)}>Put back</Button>
                      : <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => { setReason(''); setTakeOff(s); }}>Take off</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h5 className="mb-0">Reports {data.openReports > 0 && <Badge bg="warning" text="dark">{data.openReports} open</Badge>}</h5>
          <Form.Select size="sm" style={{ width: 'auto' }} value={view} onChange={(e) => setView(e.target.value)} aria-label="Show">
            <option value="OPEN">Open</option>
            <option value="DONE">Dealt with</option>
            <option value="ALL">All</option>
          </Form.Select>
        </Card.Header>
        {reports.length === 0 ? <Card.Body className="text-muted">Nothing here.</Card.Body> : (
          <Table hover responsive className="mb-0 align-middle" data-testid="directory-reports">
            <thead><tr><th>When</th><th>Product</th><th>What</th><th>From</th><th /></tr></thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td className="text-nowrap">{formatDateTime(r.createdAt)}</td>
                  <td style={{ maxWidth: '18rem' }}>
                    <a href={`/shop/${r.storeSlug}/product/${r.productId}`} target="_blank" rel="noopener noreferrer" className="fw-semibold">{r.productName || `Product ${r.productId}`}</a>
                    <div className="small text-muted">{r.storeSlug}</div>
                  </td>
                  <td style={{ maxWidth: '20rem' }}><Badge bg="secondary">{REASONS[r.reason] || r.reason}</Badge>{r.details && <div className="small mt-1" style={{ whiteSpace: 'pre-wrap' }}>{r.details}</div>}</td>
                  <td className="small">{r.reporterEmail || <span className="text-muted">Not given</span>}</td>
                  <td className="text-end text-nowrap">
                    {r.status === 'NEW' ? (
                      <div className="d-flex gap-1 justify-content-end flex-wrap">
                        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => resolve(r, 'DISMISS', 'Dismissed.')}>Dismiss</Button>
                        <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => { if (window.confirm('Take this product off the shop page? It stays in its store.')) resolve(r, 'HIDE_PRODUCT', 'The product is off the shop page.'); }}>Take product off</Button>
                        <Button size="sm" variant="danger" disabled={busy} onClick={() => { if (window.confirm('Take the whole store off the shop page? Its store and orders carry on.')) resolve(r, 'HIDE_STORE', 'The store is off the shop page.'); }}>Take store off</Button>
                      </div>
                    ) : (
                      <span className="small text-muted">{OUTCOMES[r.outcome] || r.outcome}{r.handledBy ? ` by ${r.handledBy}` : ''}{r.handledAt ? `, ${formatDateTime(r.handledAt)}` : ''}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal show={Boolean(takeOff)} onHide={() => setTakeOff(null)} centered>
        <Form onSubmit={confirmTakeOff}>
          <Modal.Header closeButton><Modal.Title className="h5">Take {takeOff?.name} off the shop page</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="small text-muted">Its store, address and orders carry on. The Org Admin sees the reason in Online Store settings.</p>
            <Form.Group controlId="takeoff-reason">
              <Form.Label>Reason</Form.Label>
              <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} required autoFocus />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="link" onClick={() => setTakeOff(null)}>Cancel</Button>
            <Button type="submit" variant="danger">Take off</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
